"use client";

import { createContext, useContext, useState, useTransition, type ReactNode } from "react";
import { Translate } from "@phosphor-icons/react";
import AlertBanner from "@/components/ui/AlertBanner";
import MarkdownBio from "@/components/ui/MarkdownBio";
import { getProfileOriginal, type ProfileOriginalText } from "@/lib/actions/profile-translation";

/**
 * A translated professional profile: the banner (owner pick C, 2026-09-22: the
 * AlertBanner `info` band the articles use, across the top of the profile) and
 * the fields it switches between the translation and the professional's own
 * words.
 *
 * ⛔ The page is sent with the TRANSLATION only. The original is fetched by a
 * server action on the first "Show original" click, so a translated profile's
 * HTML carries one language, the one its URL is for (search engines index
 * visible text whatever `lang` says; i18n-expansion-spec.md §10.4).
 *
 * ⚠️ The banner copy comes from the message files (the page is in that
 * language), the OPPOSITE of `LocaleSuggestion` and the articles'
 * "not translated" notice, which must speak a language the page may not.
 */

type Ctx = {
  showOriginal: boolean;
  original: ProfileOriginalText | null;
};
const TranslationContext = createContext<Ctx | null>(null);

export function ProfileTranslationProvider({
  slug,
  copy,
  children,
}: {
  slug: string;
  copy: { translated: string; original: string; showOriginal: string; showTranslation: string; failed: string };
  children: ReactNode;
}) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [original, setOriginal] = useState<ProfileOriginalText | null>(null);
  const [failed, setFailed] = useState(false);
  const [pending, startTransition] = useTransition();

  const toggle = () => {
    if (showOriginal) { setShowOriginal(false); return; }
    if (original) { setShowOriginal(true); return; }
    startTransition(async () => {
      const text = await getProfileOriginal(slug).catch(() => null);
      if (!text) { setFailed(true); return; }
      setFailed(false);
      setOriginal(text);
      setShowOriginal(true);
    });
  };

  return (
    <TranslationContext.Provider value={{ showOriginal, original }}>
      <AlertBanner variant="info" role="status" icon={<Translate size={14} weight="bold" />}>
        {failed ? copy.failed : showOriginal ? copy.original : copy.translated}{" "}
        <span aria-hidden="true" className="opacity-50">·</span>{" "}
        <button
          type="button"
          onClick={toggle}
          disabled={pending}
          aria-pressed={showOriginal}
          className="font-semibold underline underline-offset-2 hover:opacity-80 disabled:opacity-60"
        >
          {showOriginal ? copy.showTranslation : copy.showOriginal}
        </button>
      </AlertBanner>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * One translatable field. Shows `translated` until the reader asks for the
 * original, then the professional's own text with its `lang`.
 *
 * ⛔ `sourceLang` is PER FIELD: one profile can mix languages (a Vietnamese
 * bio under an English headline), so the original of each field carries the
 * language that field was written in. No `sourceLang` means this field is not
 * translated here: `translated` is already the original and never swaps.
 */
export function TranslatedField({
  field,
  translated,
  sourceLang,
  markdown = false,
}: {
  /** Key into the original map: a field name, "specialties.<i>" or "package.<id>.name". */
  field: string;
  translated: string;
  sourceLang?: string | null;
  markdown?: boolean;
}) {
  const ctx = useContext(TranslationContext);
  const originalText = sourceLang && ctx?.showOriginal ? ctx.original?.[field] || null : null;
  const text = originalText ?? translated;
  const lang = originalText ? sourceLang! : undefined;
  if (markdown) return <div lang={lang}><MarkdownBio markdown={text} variant="profile" /></div>;
  return <span lang={lang}>{text}</span>;
}
